import { Link } from 'react-router-dom';
import logo from '../../assets/logo.png';
import smartchoiceLogo from '../../assets/logo_smartchoice.png';
import { getAppVersionLabel } from '../../utils/appVersion';
import { openCookiePreferences } from '../../utils/cookieConsent';

const CONTACT_EMAIL = 'cybertech@smartchoice.ro';

const DISCLAIMER =
  'Surfaces publicly available OSINT metadata for professional due diligence. Raw credentials or unauthorized databases are never ingested. Temporary caches may hold investigation metadata — see Privacy Policy.';

const SPONSORS = [
  {
    href: 'https://cyber-tech.ro/en/',
    label: 'Cyber Tech',
    src: logo,
  },
  {
    href: 'https://smartchoice.ro/',
    label: 'SmartChoice',
    src: smartchoiceLogo,
  },
];

/**
 * Compact site footer — single strip on mobile; balanced layout on tablet+;
 * two-column stack on desktop (brand | legal + sponsors).
 * Privacy is always visible (not only inside a horizontal scroll lane).
 *
 * @param {{ className?: string, dense?: boolean }} props
 */
export default function Footer({ className = '', dense = false }) {
  const versionLabel = getAppVersionLabel();

  return (
    <footer
      className={`site-footer w-full min-w-0 max-w-full shrink-0 ${dense ? 'site-footer--dense' : ''} ${className}`.trim()}
      aria-label="Site footer"
      data-dense={dense ? 'true' : undefined}
    >
      <div className="site-footer-accent" aria-hidden />
      <div
        className={`site-footer-inner mx-auto flex w-full min-w-0 max-w-6xl items-center gap-x-1.5 gap-y-1 px-2 py-1 sm:grid sm:grid-cols-[minmax(0,1fr)_auto_auto] sm:items-center sm:gap-x-4 sm:px-5 sm:py-2 lg:max-w-7xl lg:grid-cols-[minmax(0,1fr)_auto] lg:items-start lg:gap-x-10 lg:px-6 lg:py-3 ${
          dense
            ? 'pb-[max(0.2rem,env(safe-area-inset-bottom))] lg:py-2'
            : 'pb-[max(0.35rem,env(safe-area-inset-bottom))] sm:pb-[max(0.5rem,env(safe-area-inset-bottom))]'
        }`}
      >
        <div className="site-footer-brand min-w-0 flex-1 basis-0 sm:flex-none sm:basis-auto lg:min-w-0">
          <div className="flex min-w-0 items-center gap-1 sm:gap-1.5 lg:gap-2">
            <span
              className="site-footer-version shrink-0 font-mono text-[10px] tabular-nums tracking-wide text-slate-300 sm:text-[11px]"
              title={
                process.env.NODE_ENV === 'production'
                  ? 'Production build stamp'
                  : 'Development build stamp'
              }
            >
              {versionLabel}
            </span>
            <p className="min-w-0 truncate text-[11px] leading-none sm:text-sm sm:leading-tight lg:overflow-visible lg:whitespace-normal lg:leading-snug">
              <span className="font-semibold tracking-wide text-cyan-200">CyberTracer.ro</span>
              <span className="site-footer-tagline hidden text-slate-300 sm:inline">
                {' '}
                — OSINT Due Diligence &amp; Digital Footprint
              </span>
            </p>
          </div>
          <p
            className="site-footer-disclaimer mt-0.5 hidden text-xs leading-snug text-slate-400 sm:line-clamp-1 sm:block lg:mt-1.5 lg:max-w-2xl lg:line-clamp-2 lg:leading-relaxed"
            title={DISCLAIMER}
          >
            {DISCLAIMER}
          </p>
        </div>

        {/*
          Mobile/tablet: `sm:contents` keeps nav + sponsors as grid siblings (3-col).
          Desktop: stacked column — legal row, then sponsors — so the strip is not one crowded line.
        */}
        <div className="site-footer-meta flex shrink-0 items-center gap-x-1.5 sm:contents lg:flex lg:flex-col lg:items-end lg:justify-self-end lg:gap-2">
          <nav
            className="site-footer-nav flex min-w-0 shrink-0 flex-wrap items-center gap-x-0.5 text-[11px] font-medium text-slate-200 sm:justify-self-center sm:text-xs lg:max-w-none lg:flex-nowrap lg:justify-end lg:gap-x-0 lg:justify-self-auto"
            aria-label="Legal"
          >
            {/* Privacy always first and always visible (phone + tablet + desktop). */}
            <Link
              to="/privacy"
              className="shrink-0 rounded px-0.5 py-0.5 font-semibold text-cyan-200 transition hover:text-cyan-100 lg:px-1"
              data-testid="footer-privacy"
            >
              Privacy
            </Link>
            <span className="mx-0.5 hidden shrink-0 text-slate-500 sm:inline sm:mx-1.5" aria-hidden>
              ·
            </span>
            <Link
              to="/about"
              className="hidden shrink-0 rounded px-0.5 py-0.5 transition hover:text-cyan-300 sm:inline lg:px-1"
            >
              About
            </Link>
            <span className="mx-0.5 hidden shrink-0 text-slate-500 sm:inline sm:mx-1.5" aria-hidden>
              ·
            </span>
            <Link
              to="/cookies"
              className="hidden shrink-0 rounded px-0.5 py-0.5 transition hover:text-cyan-300 sm:inline lg:px-1"
            >
              Cookies
            </Link>
            <span className="mx-0.5 hidden shrink-0 text-slate-500 sm:inline sm:mx-1.5" aria-hidden>
              ·
            </span>
            <Link
              to="/terms"
              className="hidden shrink-0 rounded px-0.5 py-0.5 transition hover:text-cyan-300 sm:inline lg:px-1"
            >
              Terms
            </Link>
            <span className="mx-0.5 shrink-0 text-slate-500 sm:mx-1.5" aria-hidden>
              ·
            </span>
            <button
              type="button"
              onClick={() => openCookiePreferences()}
              className="shrink-0 rounded px-0.5 py-0.5 text-left transition hover:text-cyan-300 lg:px-1"
              data-testid="footer-manage-cookies"
            >
              <span className="sm:hidden">Prefs</span>
              <span className="hidden sm:inline">Cookies prefs</span>
            </button>
            <span className="mx-1 hidden shrink-0 text-slate-500 md:inline lg:mx-1.5" aria-hidden>
              ·
            </span>
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              className="hidden shrink-0 rounded px-0.5 py-0.5 font-mono text-[10px] tracking-normal text-slate-400 transition hover:text-cyan-300 md:inline md:text-xs lg:px-1"
              title={CONTACT_EMAIL}
            >
              {CONTACT_EMAIL}
            </a>
          </nav>

          <div
            className="site-footer-sponsors flex shrink-0 items-center gap-1.5 sm:justify-self-end sm:gap-3 lg:justify-self-auto lg:pt-0.5"
            aria-label="Infrastructure sponsors"
          >
            <span className="site-footer-powered-label hidden text-[9px] font-semibold uppercase tracking-[0.12em] text-slate-500 lg:inline">
              Powered by
            </span>
            {SPONSORS.map(({ href, label, src }) => (
              <a
                key={href}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="site-footer-sponsor-link inline-flex shrink-0 items-center"
                aria-label={label}
              >
                <img
                  src={src}
                  alt=""
                  className="h-3.5 w-auto max-w-[4.5rem] object-contain sm:h-5 sm:max-w-none lg:h-[1.35rem]"
                />
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}
